import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { safeText } from '../lib/render';
import { renderAcademicMarkdown } from '../lib/markdown';

const TERMINAL_STATUSES = new Set(['completed', 'complete', 'failed', 'error']);

export default function DissertationLive() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const stopped = useRef(false);

  useEffect(() => {
    if (!id) return;
    stopped.current = false;
    let timer = null;
    let inFlight = false;

    async function poll() {
      if (stopped.current || inFlight) return;
      inFlight = true;
      try {
        const res = await api.get(`/api/dissertations/${id}/live-progress`);
        if (stopped.current) return;
        setData(res);
        setErr(null);
        if (TERMINAL_STATUSES.has((res.dissertation_status || '').toLowerCase())) {
          stopped.current = true;
          if ((res.dissertation_status || '').toLowerCase().startsWith('complet')) {
            setTimeout(() => nav(`/dissertation/${id}`), 1800);
          }
          return;
        }
      } catch (e) {
        if (e instanceof ApiError && [401, 403, 404].includes(e.status)) {
          stopped.current = true;
          setErr(e.status === 404 ? 'This dissertation no longer exists — it may have been deleted.' : e.message);
          return;
        }
        setErr(e instanceof Error ? e.message : 'Poll failed');
      } finally {
        inFlight = false;
      }
      if (!stopped.current) timer = setTimeout(poll, 3000);
    }

    void poll();
    return () => {
      stopped.current = true;
      if (timer) clearTimeout(timer);
    };
  }, [id, nav]);

  const status = (data?.dissertation_status || 'pending').toLowerCase();
  const failed = status.startsWith('fail') || status === 'error';
  const completed = status.startsWith('complet');
  const chapters = data?.chapters_json ?? {};
  const chapterKeys = Object.keys(chapters).sort(
    (a, b) => chapterNum(a) - chapterNum(b),
  );
  const currentChapter = data?.live_progress?.current_chapter;
  const totalChapters = data?.live_progress?.total_chapters ?? 5;
  const wordCount = data?.live_progress?.word_count ?? 0;
  const chaptersWithContent = chapterKeys
    .filter((k) => k !== 'front_matter' && chapterHasContent(chapters[k]));
  // Progress %: prefer backend-reported; otherwise derive from the share of
  // chapters that already have drafted content. Never stay stuck at 0 when
  // 3 of 5 chapters are visibly complete.
  const rawPercent = Number(data?.live_progress?.percent ?? 0);
  const derivedPercent = Math.round((chaptersWithContent.length / Math.max(1, totalChapters)) * 100);
  const percent = completed
    ? 100
    : rawPercent > 0
    ? Math.round(rawPercent)
    : derivedPercent;
  const generationStarted = chaptersWithContent.length > 0 || percent > 0;

  // Smart progress message: don't say "Initializing…" when content is clearly
  // being produced. Prefer backend message → current-chapter label → fallback.
  const backendMessage = safeText(
    data?.live_progress?.message ?? data?.current_status?.message ?? data?.current_status,
    '',
  );
  const message =
    backendMessage ||
    (currentChapter
      ? `Writing Chapter ${currentChapter}: ${chapters[`chapter_${currentChapter}`]?.title || chapterFallbackTitle(currentChapter)}…`
      : generationStarted
      ? `Writing your dissertation — ${chaptersWithContent.length} of ${totalChapters} chapters drafted`
      : completed
      ? 'Dissertation ready'
      : failed
      ? 'Generation failed'
      : 'Starting up…');
  // Phase label — skip "initialized" once real generation is visibly underway.
  const rawPhase = safeText(data?.live_progress?.phase ?? data?.dissertation_status ?? '');
  const phase =
    rawPhase && rawPhase.toLowerCase() === 'initialized' && generationStarted
      ? 'generating'
      : rawPhase;

  // Elapsed timer: ticks every second while generation is running.
  const elapsedSeconds = useElapsedSeconds(data?.created_at, !completed && !failed);

  return (
    <>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: 'rgba(0,0,0,0.06)',
          zIndex: 1000,
        }}
      >
        <div
          style={{
            width: `${percent}%`,
            height: '100%',
            background: failed
              ? '#ef4444'
              : 'linear-gradient(90deg, var(--primary), var(--secondary))',
            transition: 'width 0.5s ease',
          }}
        />
      </div>

      <div className="main-container">
        <div className="row">
          <div className="col-lg-10 mx-auto">
            <Link to="/dashboard" className="text-muted small">
              <i className="fas fa-arrow-left me-1"></i>Back to dashboard
            </Link>

            <div className="d-flex align-items-center justify-content-between mt-2 mb-4 flex-wrap gap-2">
              <div>
                <h1 className="mb-1" style={{ fontWeight: 700 }}>
                  {completed ? 'Dissertation ready' : failed ? 'Generation failed' : 'Generating dissertation…'}
                </h1>
                <p className="text-muted mb-0">{safeText(data?.topic, 'Preparing your document')}</p>
              </div>
              <span className={`status-badge ${status}`}>{status}</span>
            </div>

            {err && <div className="alert alert-warning">{safeText(err)}</div>}
            {failed && (
              <div className="alert alert-danger d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2">
                <div>
                  Generation failed. {safeText(data?.failure_info?.message ?? data?.failure_info, '')}
                </div>
                <ResumeButton id={id} onResume={() => window.location.reload()} />
              </div>
            )}

            {/* Queue indicator — shown when the job is waiting to be picked up
                by a worker. Populated from the durable_queue status endpoint. */}
            {status === 'queued' && data?.queue_position != null && (
              <div className="alert alert-info">
                <i className="fas fa-hourglass-half me-2"></i>
                Queued — position <strong>#{data.queue_position}</strong>
                {data.queue_depth != null ? ` of ${data.queue_depth}` : ''}.
                We'll start generating as soon as a worker is free.
              </div>
            )}

            <div className="card mb-4">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start flex-wrap mb-3 gap-2">
                  <div>
                    <div
                      className="text-muted small text-uppercase"
                      style={{ letterSpacing: 2, fontSize: 11 }}
                    >
                      {phase || 'working'}
                    </div>
                    <div className="fw-semibold mt-1">{message}</div>
                  </div>
                  <div style={{ fontSize: 32, fontWeight: 800, color: 'var(--primary)' }}>
                    {percent}%
                  </div>
                </div>
                <div className="progress" style={{ height: 10 }}>
                  <div
                    className={`progress-bar${completed ? '' : ' progress-bar-striped progress-bar-animated'}`}
                    role="progressbar"
                    style={{
                      width: `${percent}%`,
                      background: failed
                        ? '#ef4444'
                        : 'linear-gradient(90deg, var(--primary), var(--secondary))',
                    }}
                  />
                </div>

                <div className="row g-3 mt-3">
                  <StatPill
                    icon="fa-book"
                    label="Chapter"
                    value={
                      currentChapter
                        ? `${currentChapter} / ${totalChapters}`
                        : `${chaptersWithContent.length} / ${totalChapters}`
                    }
                  />
                  <StatPill
                    icon="fa-pen-nib"
                    label="Words written"
                    value={wordCount.toLocaleString()}
                  />
                  <StatPill
                    icon="fa-stopwatch"
                    label={completed ? 'Total time' : 'Elapsed'}
                    value={formatElapsed(elapsedSeconds)}
                  />
                  <StatPill
                    icon="fa-clock"
                    label="Last update"
                    value={
                      data?.last_updated
                        ? new Date(data.last_updated).toLocaleTimeString()
                        : '—'
                    }
                  />
                </div>
              </div>
            </div>

            {data?.data_fact_sheet && (
              <DataFactSheet facts={data.data_fact_sheet} />
            )}

            <LiveDocument
              chapters={chapters}
              frontMatter={chapters?.front_matter}
              totalChapters={totalChapters}
              currentChapter={currentChapter}
              running={!completed && !failed}
            />

            {completed && (
              <div className="text-center mt-4">
                <Link to={`/dissertation/${id}`} className="btn btn-cta btn-lg">
                  <i className="fas fa-book-open me-1"></i>Open your dissertation
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ────────────────────────────────────────────────────────────────
// DataFactSheet — a small card summarising the data this dissertation
// is built on. Pulled from analysis_results on the server; rendered
// once at the top of the live view so the reader always sees what
// country/period/source the methodology and findings are grounded in.
// ────────────────────────────────────────────────────────────────
function DataFactSheet({ facts }) {
  if (!facts) return null;
  const vars = (facts.variables || []).filter((v) => v && (v.name || v.code));
  return (
    <div
      className="card mb-3"
      style={{
        border: '1px solid rgba(99,102,241,0.2)',
        background: 'linear-gradient(135deg, rgba(99,102,241,0.04), rgba(16,185,129,0.03))',
      }}
    >
      <div className="card-body">
        <div className="d-flex align-items-center gap-2 mb-3">
          <i className="fas fa-database" style={{ color: 'var(--primary)' }}></i>
          <span
            className="fw-bold text-uppercase small"
            style={{ letterSpacing: '0.12em', color: 'var(--primary)' }}
          >
            Data fact sheet
          </span>
        </div>
        <div className="row g-3">
          <FactCell label="Source" value={facts.source || '—'} />
          <FactCell label="Country" value={facts.country || '—'} />
          <FactCell
            label="Period"
            value={
              facts.start_year && facts.end_year
                ? `${facts.start_year}–${facts.end_year}`
                : '—'
            }
          />
          <FactCell label="Observations" value={`${facts.n_obs || 0} ${facts.frequency || ''}`.trim()} />
        </div>
        {vars.length > 0 && (
          <div className="mt-3">
            <div className="text-muted small text-uppercase mb-2" style={{ letterSpacing: '0.08em' }}>
              Indicators analysed
            </div>
            <ul className="mb-0" style={{ listStyle: 'none', padding: 0 }}>
              {vars.map((v, i) => (
                <li key={i} className="small mb-1">
                  <span className="fw-semibold" style={{ color: 'var(--ink)' }}>
                    {v.name}
                  </span>
                  {v.code && (
                    <span
                      className="ms-2 text-muted font-mono"
                      style={{ fontSize: '0.78rem' }}
                    >
                      {v.code}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
        {facts.methodology && (
          <div
            className="mt-3 p-2 small text-muted"
            style={{ background: 'rgba(0,0,0,0.03)', borderRadius: 6 }}
          >
            <i className="fas fa-cog me-1"></i>
            {facts.methodology}
          </div>
        )}
      </div>
    </div>
  );
}

function FactCell({ label, value }) {
  return (
    <div className="col-md-3 col-6">
      <div className="text-muted small text-uppercase" style={{ letterSpacing: '0.08em', fontSize: 11 }}>
        {label}
      </div>
      <div className="fw-semibold" style={{ color: 'var(--ink)' }}>
        {value || '—'}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// LiveDocument — renders the whole dissertation as one continuous
// flow (front matter → TOC → chapters → sections) instead of separate
// cards. New sections stream into the same <article> so the user reads
// a single growing document, not a stack of boxes.
// ────────────────────────────────────────────────────────────────

const CHAPTER_FALLBACK_TITLES = [
  'Introduction',
  'Literature Review',
  'Methodology',
  'Findings & Analysis',
  'Discussion & Recommendations',
];

function LiveDocument({ chapters, frontMatter, totalChapters, currentChapter, running }) {
  // Assemble one big markdown blob that covers every available piece in
  // reading order. Memoised on a cheap fingerprint so the 1 Hz elapsed
  // timer re-render doesn't re-parse the whole document.
  const fingerprint = documentFingerprint(chapters, frontMatter);
  const { markdown, toc } = useMemo(
    () => assembleDocument(chapters, frontMatter, totalChapters, currentChapter),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fingerprint, currentChapter, totalChapters],
  );
  const hasAny = markdown.trim().length > 0;
  const rendered = useMemo(
    () => (hasAny ? renderAcademicMarkdown(markdown) : { html: '', references: [] }),
    [markdown, hasAny],
  );

  // Auto-scroll: when new content arrives, slide the user toward the
  // current-section anchor — unless they've scrolled up to read earlier
  // material, in which case we leave them alone.
  const scrollerRef = useRef(null);
  const stickToLatestRef = useRef(true);
  useEffect(() => {
    if (!running || !stickToLatestRef.current || !scrollerRef.current) return;
    const el = scrollerRef.current;
    // Try to bring the current-section marker into view
    const marker = el.querySelector('[data-live-marker="current"]');
    if (marker) {
      marker.scrollIntoView({ behavior: 'smooth', block: 'end' });
    } else {
      el.scrollTop = el.scrollHeight;
    }
  }, [fingerprint, running]);
  const onScroll = (e) => {
    const el = e.currentTarget;
    stickToLatestRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  };

  if (!hasAny) {
    return (
      <div className="card mb-3">
        <div className="card-body text-muted d-flex align-items-center gap-2">
          <div className="spinner-border spinner-border-sm text-primary" role="status" />
          <span>Waiting for the first section to arrive…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="card mb-3">
      <div
        ref={scrollerRef}
        onScroll={onScroll}
        className="p-4 p-md-5"
        style={{
          maxHeight: running ? 680 : 'none',
          overflowY: running ? 'auto' : 'visible',
          background: '#fff',
          borderRadius: 14,
        }}
      >
        {toc.length > 0 && <TableOfContents toc={toc} currentChapter={currentChapter} />}
        <article
          className="dissertation-article"
          style={{ lineHeight: 1.75, fontSize: '1rem' }}
          dangerouslySetInnerHTML={{ __html: rendered.html }}
        />
        {running && (
          <div
            data-live-marker="current"
            className="mt-4 p-3 rounded shimmer-text"
            style={{
              background: 'linear-gradient(90deg, rgba(99,102,241,0.08), rgba(99,102,241,0.02))',
              border: '1px dashed rgba(99,102,241,0.3)',
              color: 'var(--primary)',
            }}
          >
            <i className="fas fa-pen-fancy me-2"></i>
            Writing
            {currentChapter
              ? ` Chapter ${currentChapter}: ${
                  chapters?.[`chapter_${currentChapter}`]?.title ||
                  CHAPTER_FALLBACK_TITLES[currentChapter - 1] ||
                  ''
                }`
              : ' the next section'}
            …
          </div>
        )}
      </div>
    </div>
  );
}

function TableOfContents({ toc, currentChapter }) {
  return (
    <div
      className="mb-5 p-3 p-md-4"
      style={{ background: '#fbfbfd', border: '1px solid var(--border)', borderRadius: 10 }}
    >
      <div
        className="text-uppercase small fw-bold mb-3"
        style={{ letterSpacing: '0.12em', color: 'var(--muted)' }}
      >
        Table of contents
      </div>
      <ol style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
        {toc.map((ch) => (
          <li key={ch.n} style={{ marginBottom: '0.6rem' }}>
            <div
              className="fw-semibold"
              style={{ color: ch.n === currentChapter ? 'var(--primary)' : 'var(--ink)' }}
            >
              Chapter {ch.n}: {ch.title}
              {ch.n === currentChapter && (
                <span className="badge bg-primary ms-2" style={{ fontSize: '0.65rem' }}>
                  now writing
                </span>
              )}
            </div>
            {ch.sections.length > 0 && (
              <ul
                className="text-muted small"
                style={{ listStyle: 'none', paddingLeft: '0.2rem', margin: '0.2rem 0 0' }}
              >
                {ch.sections.map((s) => (
                  <li key={s.num}>
                    <span style={{ color: 'var(--muted)' }}>{s.num}</span>{' '}
                    <span style={{ color: 'var(--ink)' }}>{s.title}</span>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

// Strip RTDB-stored placeholder artifacts that don't belong in the rendered view:
//   • `[TABLE:xyz]` — tables are rendered separately from `figures`; strip
//     inline markers for now (live view just shows prose).
//   • `[FIGURE:xyz]` — same.
//   • `□cite□Author2020□` — remnants of half-rendered LaTeX-style citations
//     (the unicode squares come from the AI's tokenizer choking on braces
//     it emitted). Unwrap to `(Author, 2020)`.
function sanitizeSectionText(text) {
  if (!text || typeof text !== 'string') return '';
  let out = text;
  // LaTeX-style citations rendered as □cite□...□
  out = out.replace(/□cite□([^□]+?)□/g, (_m, body) => {
    const items = body.split(/\s+/).filter(Boolean).map((item) => {
      const yearMatch = item.match(/(\d{4})$/);
      if (yearMatch) {
        const author = item.slice(0, -4).replace(/([a-z])([A-Z])/g, '$1 $2');
        return `${author}, ${yearMatch[1]}`;
      }
      return item.replace(/([a-z])([A-Z])/g, '$1 $2');
    });
    return `(${items.join('; ')})`;
  });
  // Inline table/figure placeholders
  out = out.replace(/\[TABLE:[^\]]+\]/g, '');
  out = out.replace(/\[FIGURE:[^\]]+\]/g, '');
  // Leftover \cite{Foo2020} that never got through the stripping above
  out = out.replace(/\\cite\{([^}]+)\}/g, (_m, body) => {
    const items = body.split(/,\s*/).map((item) => {
      const yearMatch = item.match(/(\d{4})$/);
      if (yearMatch) {
        const author = item.slice(0, -4).replace(/([a-z])([A-Z])/g, '$1 $2');
        return `${author}, ${yearMatch[1]}`;
      }
      return item;
    });
    return `(${items.join('; ')})`;
  });
  // Tidy any whitespace squeeze the substitutions left behind
  out = out.replace(/[ \t]{2,}/g, ' ').replace(/\n{3,}/g, '\n\n');
  return out.trim();
}

function assembleDocument(chapters, frontMatter, totalChapters, currentChapter) {
  const parts = [];
  const toc = [];

  // ── Front matter ──
  const abstract = readText(frontMatter?.abstract);
  const acknowledgements = readText(frontMatter?.acknowledgements) || readText(frontMatter?.acknowledgments);
  const dedication = readText(frontMatter?.dedication);
  if (abstract) parts.push(`## Abstract\n\n${sanitizeSectionText(abstract)}`);
  if (acknowledgements) parts.push(`## Acknowledgements\n\n${sanitizeSectionText(acknowledgements)}`);
  if (dedication) parts.push(`## Dedication\n\n${sanitizeSectionText(dedication)}`);

  // ── Chapters ──
  for (let n = 1; n <= totalChapters; n += 1) {
    const ch = chapters?.[`chapter_${n}`];
    const chTitle = ch?.title || ch?.chapter_title || CHAPTER_FALLBACK_TITLES[n - 1] || `Chapter ${n}`;
    const sections = orderedSections(ch);
    const flatContent = readText(ch?.content);
    const hasAnything = sections.length > 0 || flatContent;
    if (!hasAnything && n !== currentChapter) continue;

    parts.push(`## Chapter ${n}: ${chTitle}`);
    const tocEntry = { n, title: chTitle, sections: [] };

    if (sections.length > 0) {
      for (const s of sections) {
        const num = s.section_number || s.number || '';
        const title = s.section_title || s.title || '';
        const body = sanitizeSectionText(readText(s.content) || readText(s.text));
        if (num && title) tocEntry.sections.push({ num, title });
        if (!body) continue;
        const heading = num && title ? `${num} ${title}` : num || title || '';
        if (heading) parts.push(`### ${heading}\n\n${body}`);
        else parts.push(body);
      }
    } else if (flatContent) {
      parts.push(sanitizeSectionText(flatContent));
    } else if (n === currentChapter) {
      // Chapter is being written but no sections yet
      parts.push(`_Writing…_`);
    }

    toc.push(tocEntry);
  }

  return { markdown: parts.join('\n\n'), toc };
}

function orderedSections(ch) {
  if (!ch || typeof ch !== 'object') return [];
  const secs = ch.sections;
  if (!secs || typeof secs !== 'object') return [];
  const entries = Object.entries(secs).map(([k, v]) => {
    if (!v || typeof v !== 'object') return null;
    return { key: k, ...v };
  }).filter(Boolean);
  entries.sort((a, b) => sectionOrder(a.section_number || a.key) - sectionOrder(b.section_number || b.key));
  return entries;
}

function readText(v) {
  if (!v) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'object') return v.content || v.text || '';
  return '';
}

function documentFingerprint(chapters, frontMatter) {
  if (!chapters) return '';
  const fm = frontMatter
    ? [frontMatter.abstract, frontMatter.acknowledgements, frontMatter.acknowledgments, frontMatter.dedication]
        .map((v) => (typeof v === 'string' ? v.length : 0))
        .join(',')
    : '';
  const chParts = Object.entries(chapters)
    .filter(([k]) => k !== 'front_matter')
    .sort()
    .map(([k, ch]) => {
      if (!ch || typeof ch !== 'object') return `${k}:0`;
      const secLen = ch.sections
        ? Object.entries(ch.sections)
            .map(([sk, s]) => {
              const l = typeof s === 'string' ? s.length : (s?.content?.length ?? s?.text?.length ?? 0);
              return `${sk}:${l}`;
            })
            .join(',')
        : '';
      return `${k}:${ch.word_count ?? 0}:${ch.content?.length ?? 0}:${secLen}`;
    })
    .join('|');
  return `fm=${fm}||${chParts}`;
}

function chapterFingerprint(ch) {
  if (!ch || typeof ch !== 'object') return '';
  const secs = ch.sections;
  const secFingerprint = !secs
    ? ''
    : Object.keys(secs)
        .sort()
        .map((k) => {
          const s = secs[k];
          const len =
            typeof s === 'string'
              ? s.length
              : typeof s === 'object'
              ? (s?.content?.length ?? s?.text?.length ?? 0)
              : 0;
          return `${k}:${len}`;
        })
        .join(',');
  return `${ch.word_count ?? 0}|${ch.content?.length ?? 0}|${secFingerprint}`;
}

const ChapterLiveCard = memo(
  ChapterLiveCardInner,
  (prev, next) =>
    prev.n === next.n &&
    prev.currentChapter === next.currentChapter &&
    prev.running === next.running &&
    prev.ch?.title === next.ch?.title &&
    chapterFingerprint(prev.ch) === chapterFingerprint(next.ch),
);

function ChapterLiveCardInner({ n, ch, currentChapter, running }) {
  // Assemble full chapter markdown from either `content` or ordered `sections`.
  const fullText = useMemo(() => assembleChapterText(ch), [ch]);
  const hasAny = fullText.trim().length > 0;
  const sectionCount = ch?.sections ? Object.keys(ch.sections).length : 0;

  const wc =
    typeof ch?.word_count === 'number' && ch.word_count > 0
      ? ch.word_count
      : hasAny
      ? fullText.split(/\s+/).filter(Boolean).length
      : 0;

  const isCurrent = currentChapter === n;
  const isWritten = hasAny && (!running || !isCurrent);
  // Current chapter (or any chapter with partial content while running):
  // expand by default so the user sees the live stream.
  const expandByDefault = isCurrent || (running && hasAny && !isWritten);
  const [expanded, setExpanded] = useState(expandByDefault);
  // Re-apply the default when a chapter transitions from queued → current.
  useEffect(() => {
    if (expandByDefault) setExpanded(true);
  }, [expandByDefault]);

  // Auto-scroll the preview to the bottom as new text arrives — but only
  // when the user is already near the bottom. If they have scrolled up to
  // read earlier prose, we leave their scroll position alone so the UI
  // doesn't yank them back on every poll.
  const scrollerRef = useRef(null);
  const stickToBottomRef = useRef(true);
  useEffect(() => {
    if (!expanded || !isCurrent) return;
    const el = scrollerRef.current;
    if (!el || !stickToBottomRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [fullText, expanded, isCurrent]);
  const onScroll = (e) => {
    const el = e.currentTarget;
    // Within 80px of the bottom = still "stuck"; further up = user is reading
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const rendered = useMemo(
    () => (hasAny ? renderAcademicMarkdown(fullText) : { html: '', references: [] }),
    [fullText, hasAny],
  );

  const border = isWritten
    ? '4px solid #22c55e'
    : isCurrent
    ? '4px solid var(--accent)'
    : '4px solid var(--border)';

  return (
    <div className="card mb-3" style={{ borderLeft: border }}>
      <div
        className="card-body d-flex justify-content-between align-items-center flex-wrap gap-2"
        style={{ cursor: hasAny ? 'pointer' : 'default' }}
        onClick={() => hasAny && setExpanded((v) => !v)}
      >
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          <div className="fw-semibold">
            Chapter {n}: {ch?.title || chapterFallbackTitle(n)}
          </div>
          <div className="text-muted small mt-1">
            {isWritten ? (
              <>
                <i className="fas fa-check-circle text-success me-1"></i>
                {wc.toLocaleString()} words
                {sectionCount > 0 && <> · {sectionCount} sections</>}
              </>
            ) : isCurrent && hasAny ? (
              <span className="shimmer-text">
                <i className="fas fa-pen-fancy me-1"></i>
                Writing now — {wc.toLocaleString()} words so far
                {sectionCount > 0 && <> · {sectionCount} sections</>}
              </span>
            ) : isCurrent ? (
              <span className="shimmer-text">
                <i className="fas fa-pen-fancy me-1"></i>Writing now…
              </span>
            ) : hasAny ? (
              <>
                <i className="fas fa-file-alt me-1"></i>
                Draft captured — {wc.toLocaleString()} words
              </>
            ) : (
              <>
                <i className="fas fa-hourglass-half me-1"></i>Queued
              </>
            )}
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          {isCurrent && running && (
            <div
              className="spinner-border text-primary"
              role="status"
              style={{ width: 24, height: 24 }}
            >
              <span className="visually-hidden">Loading…</span>
            </div>
          )}
          {hasAny && (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={(e) => {
                e.stopPropagation();
                setExpanded((v) => !v);
              }}
              aria-label={expanded ? 'Hide preview' : 'Show preview'}
            >
              <i className={`fas ${expanded ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
            </button>
          )}
        </div>
      </div>
      {isCurrent && running && (
        <div
          style={{
            height: 4,
            background: 'linear-gradient(90deg, transparent, var(--accent), transparent)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 2s linear infinite',
          }}
        />
      )}
      {expanded && hasAny && (
        <div
          ref={scrollerRef}
          onScroll={onScroll}
          className="px-4 py-3"
          style={{
            maxHeight: isCurrent && running ? 420 : 600,
            overflowY: 'auto',
            borderTop: '1px solid var(--border)',
            background: '#fbfbfd',
            lineHeight: 1.7,
            fontSize: '0.95rem',
          }}
        >
          <article
            className="dissertation-article"
            dangerouslySetInnerHTML={{ __html: rendered.html }}
          />
          {isCurrent && running && (
            <div className="text-muted small mt-2 shimmer-text">
              <i className="fas fa-pen-fancy me-1"></i>Still writing…
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Assemble a chapter's live markdown from either `content` or `sections`.
// Sections are ordered by their numeric key (1.1, 1.2, …) so later-arriving
// sections appear at the bottom of the preview.
function assembleChapterText(ch) {
  if (!ch || typeof ch !== 'object') return '';
  if (typeof ch.content === 'string' && ch.content.trim()) return ch.content;
  const sections = ch.sections;
  if (!sections || typeof sections !== 'object') return '';
  const entries = Object.entries(sections);
  entries.sort(([a], [b]) => sectionOrder(a) - sectionOrder(b));
  return entries
    .map(([key, s]) => {
      if (!s) return '';
      const title = s.title || key;
      const body = typeof s === 'string' ? s : s.content || s.text || '';
      if (!body) return '';
      return `## ${title}\n\n${body}`;
    })
    .filter(Boolean)
    .join('\n\n');
}

function sectionOrder(key) {
  // "1.3" → 1.3 ; "intro_1" → 1 ; "background" → Infinity (tail)
  const m = String(key).match(/(\d+(?:\.\d+)?)/);
  return m ? Number(m[1]) : Number.MAX_SAFE_INTEGER;
}

function StatPill({ icon, label, value }) {
  return (
    <div className="col-md-3 col-6">
      <div
        className="d-flex align-items-center gap-3 p-3"
        style={{ background: 'var(--light)', borderRadius: 10 }}
      >
        <div
          style={{
            width: 42,
            height: 42,
            borderRadius: 10,
            background: 'rgba(79,70,229,0.12)',
            color: 'var(--primary)',
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
          }}
        >
          <i className={`fas ${icon}`}></i>
        </div>
        <div style={{ minWidth: 0 }}>
          <div className="text-muted small">{label}</div>
          <div className="fw-bold" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</div>
        </div>
      </div>
    </div>
  );
}

// Returns elapsed seconds since `startIso`, ticking every second while `running`.
// If no start time is known yet, waits until one is provided on the next render.
function useElapsedSeconds(startIso, running) {
  // Use state only as a re-render trigger. Always read the real current
  // time at render so a background-tab-throttled interval or a missed tick
  // can't leave the displayed elapsed value stale — on every render we
  // compute against Date.now() fresh. Visibility changes also nudge a
  // re-render so flipping back to the tab shows the correct time.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => setTick((v) => v + 1), 1000);
    const onVis = () => setTick((v) => v + 1);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [running]);
  if (!startIso) return 0;
  const start = Date.parse(startIso);
  if (!Number.isFinite(start)) return 0;
  return Math.max(0, Math.floor((Date.now() - start) / 1000));
}

function formatElapsed(totalSeconds) {
  if (!totalSeconds || totalSeconds < 0) return '0s';
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

// True if the chapter has any usable draft content (flat text or populated sections).
function chapterHasContent(ch) {
  if (!ch || typeof ch !== 'object') return false;
  if (typeof ch.content === 'string' && ch.content.trim()) return true;
  const secs = ch.sections;
  if (secs && typeof secs === 'object') {
    for (const s of Object.values(secs)) {
      if (!s) continue;
      if (typeof s === 'string' && s.trim()) return true;
      if (typeof s === 'object' && (s.content || s.text)) return true;
    }
  }
  return false;
}

function chapterNum(key) {
  const m = key.match(/(\d+)/);
  return m ? Number(m[1]) : 0;
}

function chapterFallbackTitle(n) {
  const titles = [
    'Introduction',
    'Literature Review',
    'Methodology',
    'Findings & Analysis',
    'Discussion & Recommendations',
  ];
  return titles[n - 1] || `Chapter ${n}`;
}

function ResumeButton({ id, onResume }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  async function click() {
    if (busy) return;
    setBusy(true);
    setMsg(null);
    try {
      // Web-compatible endpoint (session/Bearer both accepted). Server picks
      // up from the last completed phase — no credits re-deducted.
      const res = await api.post(`/api/dissertation/${id}/resume`, {});
      setMsg(res?.message || 'Retry started — this one is on us.');
      if (onResume) setTimeout(onResume, 1200);
    } catch (e) {
      setMsg(e?.message || 'Retry failed');
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="d-flex align-items-center gap-2">
      <button
        type="button"
        className="btn btn-warning"
        onClick={click}
        disabled={busy}
        title="Re-run generation — free, this one was on us"
      >
        <i className={`fas ${busy ? 'fa-spinner fa-spin' : 'fa-redo'} me-1`}></i>
        {busy ? ' Retrying…' : ' Retry (free)'}
      </button>
      {msg && <span className="small text-muted">{msg}</span>}
    </div>
  );
}
